import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useActionData, useLoaderData, useSubmit } from 'react-router';
import { useIntl, FormattedMessage } from 'react-intl';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'client/components/aria/Button/Button';
import Alert from 'client/components/aria/Alert/Alert';
import Link from 'client/components/aria/Link/Link';
import DateInput from 'client/components/GDS/DateInput/DateInput';
import TextArea from 'client/components/GDS/TextArea/TextArea';
import TextInput from 'client/components/GDS/TextInput/TextInput';
import { jsonToFormData } from 'client/utils/helpers/Enquiry/Enquiry';
import { CREATE_ENQUIRY_PAGE } from 'client/utils/routes/routeConstants';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import { useSelector } from 'react-redux';
import ActivityType, { CHOOSE_OPTION } from '../EnquiryActivityType/EnquiryActivityType';
import EnquirySummary from 'client/pages/EnquiriesPage/ViewEnquiryPage/EnquirySummary/EnquirySummary';
import EnquiryDetails from 'client/pages/EnquiriesPage/components/EnquiryDetails/EnquiryDetails';
import Details from 'client/components/GDS/Details/Details';
import useAuth from 'client/utils/auth/useAuth';

const EnquiryForm = ({ enquiry, onChange, setErrors, showSummary, setShowSummary, changeEnquiryType, canChangeEnquiryType, hasBothRoles, ...props }) => {
    const submit = useSubmit();
    const intl = useIntl();
    const { activityTypes } = useLoaderData();
    const { featuresByAssetType } = useActionData() || {};
    const { assetsInAOIOwners } = useSelector(state => state?.assetOwners?.data) || {};
    const { user } = useAuth();

    const formik = useFormik({
        // set the initial values for the form.
        initialValues: {
            title: enquiry?.title || '',
            jobReference: enquiry?.jobReference || '',
            description: enquiry?.description || '',
            startDate: enquiry?.startDate || '', // new Date().toISOString(),
            endDate: enquiry?.endDate || '', // new Date().toISOString(),
            activityType: enquiry?.activityType || '', // || '2f21eb0c-ac24-404b-94b9-756aa75f68d9', // use this value during dev so you don't have to keep choosing
            isEmergency: enquiry?.isEmergency ?? enquiry?.emergency ?? false,
            geometry: enquiry?.geometry || null,
            userJourneyStartTime: enquiry?.userJourneyStartTime || '',
            userJourneyTransId: enquiry?.userJourneyTransId || ''
        },
        // define the validation using Yup.
        validationSchema: Yup.object({
            title: Yup
                .string()
                .trim()
                .max(255, intl.formatMessage({ id: 'EnquiryPageForm.title.validation.length' }, {
                    characterCount: 255
                }))
                .required(intl.formatMessage({ id: 'EnquiryPageForm.title.validation.required' })),
            description: Yup
                .string()
                .trim()
                .max(200, intl.formatMessage({ id: 'EnquiryPageForm.description.validation.length' }, {
                    characterCount: 200
                }))
                .required(intl.formatMessage({ id: 'EnquiryPageForm.description.validation.required' })),
            jobReference: Yup
                .string()
                .max(100, intl.formatMessage({ id: 'EnquiryPageForm.jobReference.validation.length' }, {
                    characterCount: 100
                })),
            startDate: Yup
                .date()
                .nullable()
                .transform((value, originalValue) => originalValue ? value : null)
                .when('isEmergency', {
                    is: false,
                    then: schema => schema.required(intl.formatMessage({ id: 'EnquiryPageForm.startDate.validation.required' })),
                    otherwise: schema => schema
                }),
            endDate: Yup
                .date()
                .nullable()
                .transform((value, originalValue) => originalValue ? value : null)
                .when(['isEmergency', 'startDate'], (isEmergency, startDate, schema) => {
                    let updatedSchema = schema;
                    if (!isEmergency && startDate) {
                        updatedSchema = updatedSchema.min(startDate, intl.formatMessage({ id: 'EnquiryPageForm.endDate.validation.afterStartDate' }));
                    }
                    if (!isEmergency) {
                        updatedSchema = updatedSchema.required(intl.formatMessage({ id: 'EnquiryPageForm.endDate.validation.required' }));
                    }
                    return updatedSchema;
                }),
            geometry: Yup
                .object()
                .nullable()
                .required(intl.formatMessage({ id: 'EnquiryPageForm.geometry.required' })),
            activityType: Yup
                .string()
                .notOneOf([CHOOSE_OPTION], intl.formatMessage({ id: 'EnquiryPageForm.activityType.required' }))
                .required(intl.formatMessage({ id: 'EnquiryPageForm.activityType.required' }))
        }),
        onSubmit: values => {
            const formData = jsonToFormData(values);

            submit(formData, {
                action: CREATE_ENQUIRY_PAGE,
                method: 'POST'
            });
        }
    });

    const handleDateChange = (dateValue, name) => {
        const { year, month, day, valid } = dateValue?.value;
        if (!valid) {
            formik.setFieldValue(name, '');
        } else {
            const date = new Date(year, month - 1, day);
            date.setHours(0, 0, 0, 0);
            formik.setFieldValue(name, date.toISOString());
        }
    };

    const handleChange = (name, value) => {
        formik.setFieldValue(name, value);
    };

    const handleChangeFromEvent = (event) => {
        const { name, value } = event.target;
        formik.setFieldValue(name, value);
    };

    useEffect(() => {
        // Need to validate form when changed. Has to be here to prevent infinite useeffect and update
        if (onChange) {
            formik.validateForm(formik.values);
        }
    }, [enquiry]);

    const scrollToTop = () => {
        const navigationElement = document.getElementById('navigation');
        if (navigationElement) {
            navigationElement.scrollIntoView();
        }
    };

    const handleShowSummary = () => {
        const touchedFields = Object.keys(formik.values).reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {});

        formik.validateForm(formik.values).then(errors => {
            formik.setTouched(touchedFields);
            const validationErrors = [];

            Object.keys(formik.errors).forEach(field => {
                validationErrors.push({ id: field, message: formik.errors[field] });
            });

            if (Object.keys(errors).length > 0) {
                formik.setErrors(errors);
                setErrors(validationErrors);
                scrollToTop();
            } else {
                setShowSummary(true);
                setErrors([]);
                scrollToTop();
            }
        });
    };

    const handleBack = () => {
        setShowSummary(!showSummary);
        document.getElementById('navigation').scrollIntoView();
    };

    useEffect(() => {
        if (onChange) {
            onChange(formik.values);
        }
    }, [formik.values]);

    const formikStartValue = useMemo(() => formik.values.startDate === '' ? null : new Date(formik.values.startDate),
        [formik.values.startDate]); // Prevents always entering useEffect with props.value, as will be a 'new' date object everytime
    const formikEndValue = useMemo(() => formik.values.endDate === '' ? null : new Date(formik.values.endDate),
        [formik.values.endDate]);
    return (
        <form onSubmit={formik.handleSubmit} data-testid={props['data-testid']}>
            {showSummary === false
                ? <>
                    <div>
                        {!hasBothRoles && (
                            <>
                                {user?.organisationName && (
                                    <div className="govuk-body govuk-!-margin-bottom-6">
                                        <p className="govuk-body">
                                            <FormattedMessage
                                                id='createWorkpackPage.purpose.currentOrganisation'
                                                values={{ organisationName: user.organisationName }}
                                            />
                                        </p>
                                    </div>
                                )}
                                <Details className="govuk-!-margin-top-10" summaryText={intl.formatMessage({ id: 'createEnquiryPage.more.info.about.enquiry.types' })}>
                                    <p className="govuk-body govuk-!-margin-top-2">
                                        <FormattedMessage id='createEnquiryPage.enquiry.type.info.unplanned'/>
                                    </p>
                                </Details>
                            </>
                        )}
                        <EnquiryDetails>
                            {intl.formatMessage({ id: 'createEnquiryPage.enquiry.details' })}
                        </EnquiryDetails>
                    </div>
                    <div className="govuk-grid-row">
                        <div className="govuk-grid-column-two-thirds">
                            <TextInput
                                label={intl.formatMessage({ id: 'EnquiryPageForm.title.label' })}
                                description={intl.formatMessage({ id: 'EnquiryPageForm.title.description' })}
                                id='title'
                                name='title'
                                autoComplete='off'
                                onChange={handleChangeFromEvent}
                                onBlur={formik.handleBlur}
                                value={formik.values.title}
                                errorMessage={formik.touched.title && formik.errors.title ? formik.errors.title : null}
                                data-testid='title'
                            />
                        </div>
                    </div>
                    <div className="govuk-grid-row">
                        <div className="govuk-grid-column-two-thirds">
                            <TextInput
                                label={intl.formatMessage({ id: 'EnquiryPageForm.jobReference.label' })}
                                description={intl.formatMessage({ id: 'EnquiryPageForm.jobReference.description' })}
                                id='jobReference'
                                name='jobReference'
                                autoComplete='off'
                                onChange={handleChangeFromEvent}
                                onBlur={formik.handleBlur}
                                value={formik.values.jobReference}
                                errorMessage={formik.touched.jobReference && formik.errors.jobReference ? formik.errors.jobReference : null}
                                data-testid='jobReference'
                            />
                        </div>
                    </div>
                    <div className="govuk-grid-row">
                        <div className="govuk-grid-column-two-thirds">
                            <ActivityType onActivityTypeChange={(value) => handleChange('activityType', value)}
                                errorMessage={formik.touched.activityType && formik.errors.activityType ? formik.errors.activityType : null}
                                activityType={formik.values.activityType}
                            />
                        </div>
                    </div>
                    <div className="govuk-grid-row">
                        <div className="govuk-grid-column-two-thirds">
                            <TextArea
                                label={intl.formatMessage({ id: 'EnquiryPageForm.description.label' })}
                                description={intl.formatMessage({ id: 'EnquiryPageForm.description.description' })}
                                id='description'
                                name='description'
                                onChange={handleChangeFromEvent}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                                errorMessage={formik.touched.description && formik.errors.description ? formik.errors.description : null}
                                data-testid='description'
                            />
                        </div>
                    </div>
                    <div className="govuk-grid-row">
                        {!formik.values.isEmergency && (
                            <div className="govuk-grid-column-two-thirds">
                                <DateInput
                                    label={intl.formatMessage({ id: 'EnquiryPageForm.startDate.label' })}
                                    description={intl.formatMessage({ id: 'EnquiryPageForm.startDate.description' })}
                                    id='startDate'
                                    name='startDate'
                                    onChange={val => handleDateChange(val, 'startDate')}
                                    onBlur={formik.handleBlur}
                                    value={formikStartValue}
                                    errorMessage={formik.touched.startDate && formik.errors.startDate ? formik.errors.startDate : null}
                                    data-testid='startDate'
                                />
                            </div>
                        )}
                    </div>
                    <div className="govuk-grid-row">
                        {!formik.values.isEmergency && (
                            <div className="govuk-grid-column-two-thirds">
                                <DateInput
                                    label={intl.formatMessage({ id: 'EnquiryPageForm.endDate.label' })}
                                    description={intl.formatMessage({ id: 'EnquiryPageForm.endDate.description' })}
                                    id='endDate'
                                    name='endDate'
                                    onChange={val => handleDateChange(val, 'endDate')}
                                    onBlur={formik.handleBlur}
                                    value={formikEndValue}
                                    errorMessage={formik.touched.endDate && formik.errors.endDate ? formik.errors.endDate : null}
                                    data-testid='endDate'
                                />
                            </div>
                        )}
                    </div>
                </>
                : <>
                    <EnquirySummary
                        enquiry={enquiry}
                        activityTypes={activityTypes}
                        showSummary={!enquiry?.enquiryId}
                        change={setShowSummary}
                        changeEnquiryType={changeEnquiryType}
                        canChangeEnquiryType={canChangeEnquiryType}
                        featuresByAssetType={featuresByAssetType}
                        assetOwners={assetsInAOIOwners}
                    />
                </>
            }
            <div className="govuk-grid-row">
                <div className="govuk-grid-column-full">
                    {showSummary === true && (
                        <>
                            <Alert
                                title={intl.formatMessage({ id: 'EnquiryPageForm.preSubmitAlert.title' })}
                                variant="secondary"
                            >
                                <p className='govuk-body'>
                                    <strong>
                                        <FormattedMessage
                                            id="EnquiryPageForm.preSubmitAlert.intro"
                                            values={{
                                                link: (chunks) => (
                                                    <Link
                                                        href="https://www.hse.gov.uk/pubns/priced/hsg47.pdf"
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {chunks}
                                                    </Link>
                                                )
                                            }}
                                        />
                                    </strong>
                                </p>
                                <ul className="govuk-list govuk-list--bullet">
                                    <li>{intl.formatMessage({ id: 'EnquiryPageForm.preSubmitAlert.bullet.one' })}</li>
                                    <li>{intl.formatMessage({ id: 'EnquiryPageForm.preSubmitAlert.bullet.two' })}</li>
                                </ul>
                            </Alert>
                            <p className='govuk-body'>
                                {intl.formatMessage({ id: 'EnquiryPageForm.preSubmitAlert.footer.prefix' })}{' '}
                                <Link href="/terms">
                                    {intl.formatMessage({ id: 'EnquiryPageForm.preSubmitAlert.termsLink' })}
                                </Link>.{' '}
                                {intl.formatMessage({ id: 'EnquiryPageForm.preSubmitAlert.footer.suffix' })}
                            </p>
                        </>
                    )}
                    <div className="govuk-button-group govuk-!-margin-top-4">
                        {showSummary === false
                            ? <>
                                <Button
                                    variant="default"
                                    type="button"
                                    onPress={() => handleShowSummary()}
                                    isDisabled={
                                        !formik.values.title ||
                                        !formik.values.description ||
                                        !formik.values.geometry ||
                                        !formik.values.activityType ||
                                        formik.values.activityType === CHOOSE_OPTION ||
                                        (!formik.values.isEmergency && (!formik.values.startDate || !formik.values.endDate))
                                    }
                                    data-testid='summaryButton'
                                >
                                    {intl.formatMessage({ id: 'general.continue' })}
                                </Button>
                            </>
                            : <>
                                <Button
                                    variant="default"
                                    onPress={formik.submitForm}
                                    data-testid='submitButton'
                                >
                                    {intl.formatMessage({ id: 'EnquiryPageForm.submitButton.label' })}
                                </Button>
                                <Button
                                    variant="link"
                                    type="button"
                                    onPress={() => handleBack()}
                                    data-testid='summaryButton'
                                >
                                    {intl.formatMessage({ id: 'general.back' })}
                                </Button>
                            </>
                        }
                    </div>
                </div>
            </div>
        </form>
    );
};

EnquiryForm.propTypes = {
    ...defaultAriaProps,
    'data-testid': PropTypes.string,
    hasBothRoles: PropTypes.bool
};

export default EnquiryForm;
